"use client";

import CoverageCard from "@/src/components/cards/coverage-card/coverage-card";
import PatientCard from "@/src/components/cards/patient-card/patient-card";
import ReferralCard from "@/src/components/cards/referral-card/referral-card";
import { CopyableContent } from "@/src/components/copyable-content";
import { EhrInstructionsCard } from "@/src/components/ehr-instructions-card";
import { ReferralProviderCards } from "@/src/components/tasks/referral-provider-cards";
import { useFaxLocationAssignment } from "@/src/lib/data-fetchers/location-assignment-fetcher";
import { formatDateForFilename } from "@/src/lib/utils/date-and-phone-utils";
import { isStatOrUrgentPriority, Referral, SchedulingPriority } from "@/src/models/documents/referral";
import { Task } from "@/src/models/task";

function getDiagnosisText(referral: Referral): string {
  if (referral.diagnosisList && referral.diagnosisList.length > 0) {
    return referral.diagnosisList
      .map((d: { diagnosisText: string | null; diagnosisCode: string | null }) => d.diagnosisText)
      .filter(Boolean)
      .join(", ");
  }

  return referral.reasonForReferral || "";
}

function getReferralOrderFacility(locationName: string): string {
  if (!locationName) {
    return "Heartplace OF";
  }

  return `Heartplace ${locationName} OF`;
}

function getSchedulingPriorityText(referral: Referral): string {
  const priority = referral.schedulingPriority || SchedulingPriority.ROUTINE;

  if (isStatOrUrgentPriority(priority)) {
    return `${priority} → If stat or urgent, change priority`;
  }

  return priority;
}

function TrainingReferralInstructionsContent({ referral, task }: { referral: Referral; task: Task }) {
  const formattedDateOfReferral = formatDateForFilename(referral.dateOfReferral);

  const { referralLocation } = useFaxLocationAssignment(task.id);

  const fileName = `${formattedDateOfReferral} Referral`;
  const locationName = referralLocation || "";

  const diagnosisText = getDiagnosisText(referral);

  const assignToNoAppointment = locationName ? `HP ${locationName}, Referrals` : "HP Referrals";
  const assignToReferralAuth = locationName ? `HP Auth, ${locationName}` : "HP Auth";
  const assignToNotReferralAuth = locationName ? `HP ${locationName}, Referrals` : "HP Referrals";

  const facilityName = getReferralOrderFacility(locationName);
  const schedulingPriority = getSchedulingPriorityText(referral);

  return (
    <div className="flex flex-col gap-4">
      <span className="font-medium text-center">{"Valerie Health - Training"} Referral Instructions:</span>
      <div className="text-muted-foreground space-y-4">
        <div>
          <span className="font-semibold">1. Create or Update the patient in eCW: </span>
          <a
            href="#"
            onClick={(e) => e.preventDefault()}
            className="text-primary hover:underline cursor-not-allowed opacity-75"
            title="Training mode - link disabled"
          >
            here (Training)
          </a>
        </div>

        <div className="flex flex-col gap-2">
          <span className="font-semibold text-foreground">No Upcoming Appointment</span>

          <div className="flex flex-col gap-2 mt-2">
            <span className="font-semibold">Upload the file to the Referrals Documents folder</span>
            <div className="ml-4 space-y-2">
              <div>
                <span className="font-medium">Assign to:</span> <CopyableContent text={assignToNoAppointment} />
              </div>
              <div>
                <span className="font-medium">File name:</span> <CopyableContent text={fileName} />
              </div>
            </div>
          </div>

          <div className="flex flex-col gap-2 mt-2">
            <span className="font-semibold">Create a referral order</span>
            <div className="ml-4 space-y-2">
              <div>
                <span className="font-medium">Assign to:</span> <CopyableContent text={assignToNoAppointment} />
              </div>
              <div>
                <span className="font-medium">Facility:</span> <CopyableContent text={facilityName} />
              </div>
              <div>
                <span className="font-medium">Reason:</span> <CopyableContent text="Needs call back from office" />
              </div>
              <div>
                <span className="font-medium">Diagnosis:</span> <CopyableContent text={diagnosisText} />
              </div>
              <div>
                <span className="font-medium">Scheduling Priority:</span> <CopyableContent text={schedulingPriority} />
              </div>
            </div>
          </div>
        </div>

        <div className="flex flex-col gap-2">
          <span className="font-semibold text-foreground">
            Upcoming Appointment → do not create a referral order instead:
          </span>

          <div className="flex flex-col gap-2 mt-2">
            <span className="font-semibold">Is the document a referral authorization?</span>

            <div className="flex flex-col gap-2 ml-4">
              <span className="font-semibold text-foreground">YES – It is a referral auth</span>
              <div className="flex flex-col gap-2 mt-2">
                <span className="font-semibold">Upload the file to the Referrals Documents folder</span>
                <div className="ml-4 space-y-2">
                  <div>
                    <span className="font-medium">Assign to:</span> <CopyableContent text={assignToReferralAuth} />
                  </div>
                  <div>
                    <span className="font-medium">File name:</span> <CopyableContent text={fileName} />
                  </div>
                </div>
              </div>
            </div>

            <div className="flex flex-col gap-2 ml-4">
              <span className="font-semibold text-foreground">NO – It is NOT a referral auth</span>
              <div className="flex flex-col gap-2 mt-2">
                <span className="font-semibold">Upload the file in Patient Docs, under the Referrals folder</span>
                <div className="ml-4 space-y-2">
                  <div>
                    <span className="font-medium">Assign to:</span> <CopyableContent text={assignToNotReferralAuth} />
                  </div>
                  <div>
                    <span className="font-medium">File name:</span> <CopyableContent text={fileName} />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function TrainingReferralCards({ referral }: { referral: Referral }) {
  return (
    <>
      <PatientCard patient={referral.patient} />
      <ReferralProviderCards referral={referral} />
      <ReferralCard referral={referral} isEditable />
      {referral.primaryCoverage && <CoverageCard coverage={referral.primaryCoverage!} type="Primary" isEditable />}
      {referral.secondaryCoverage && (
        <CoverageCard coverage={referral.secondaryCoverage!} type="Secondary" isEditable />
      )}
    </>
  );
}

interface TrainingReferralInstructionsProps {
  referral: Referral;
  task: Task;
}

export function TrainingReferralInstructions({ referral, task }: TrainingReferralInstructionsProps) {
  return (
    <div className="flex flex-col gap-8 w-full sm:min-w-[800px] max-w-[1200px] mx-auto">
      <EhrInstructionsCard>
        <div className="text-sm text-muted-foreground">
          <TrainingReferralInstructionsContent referral={referral} task={task} />
        </div>
      </EhrInstructionsCard>

      <TrainingReferralCards referral={referral} />
    </div>
  );
}
